import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { Observable, BehaviorSubject, Subject } from "rxjs";
import { tap } from "rxjs/operators";
import { HelpNavigationItem, HelpItem, HelpCategory, HelpQuerySpec } from "../models/help-library.model";

@Injectable({
    providedIn: 'root'
})
export class HelpLibraryService {

    private _activeNavItem: HelpNavigationItem | null;
    private _activeNavItem$: BehaviorSubject<HelpNavigationItem | null> = new BehaviorSubject<HelpNavigationItem | null>(null);

    categoryCreated$: Subject<HelpCategory> = new Subject<HelpCategory>();
    categoryUpdated$: Subject<HelpCategory> = new Subject<HelpCategory>();
    helpCreated$: Subject<HelpItem> = new Subject<HelpItem>();
    helpDeleted$: Subject<string> = new Subject<string>();
    helpUpdated$: Subject<HelpItem> = new Subject<HelpItem>();

    get onCategoryCreated(): Observable<HelpCategory> { return this.categoryCreated$.asObservable(); }
    get onCategoryUpdated(): Observable<HelpCategory> { return this.categoryUpdated$.asObservable(); }
    get onHelpCreated(): Observable<HelpItem> { return this.helpCreated$.asObservable(); }
    get onHelpDeleted(): Observable<string> { return this.helpDeleted$.asObservable(); }
    get onHelpUpdated(): Observable<HelpItem> { return this.helpUpdated$.asObservable(); }

    constructor(public readonly http: HttpClient) { }

    getActiveNavigationItem(): Observable<HelpNavigationItem | null> {
        return this._activeNavItem$.asObservable();
    }

    setActiveNavItem(data: HelpNavigationItem | null): void {
        this._activeNavItem = data;
        this._activeNavItem$.next(this._activeNavItem);
    }

    createCategory(item: HelpCategory): Observable<HelpCategory> {
        return this.saveCategory(item).pipe(tap((newCategory) => {
            this.categoryCreated$.next(newCategory);
        }));
    }

    createHelpItem(item: HelpItem): Observable<HelpItem> {
        return this.saveHelpItem(item).pipe(tap((newItem) => {
            this.helpCreated$.next(newItem);
        }));
    }

    deleteHelpItem(item: HelpItem): Observable<string> {
        return this.http.post<string>("/api/help/deleteItem", item).pipe(tap((id) => {
            this.helpDeleted$.next(id);
        }));
    }

    editCategory(item: HelpCategory): Observable<HelpCategory> {
        return this.saveCategory(item).pipe(tap((editedCategory) => {
            this.categoryUpdated$.next(editedCategory);
        }));
    }

    editHelpItem(item: HelpItem): Observable<HelpItem> {
        return this.saveHelpItem(item).pipe(tap((editedItem) => {
            this.helpUpdated$.next(editedItem);
        }));
    }

    getCategories(): Observable<HelpCategory[]> {
        return this.http.get<HelpCategory[]>("/api/help/getCategories");
    }

    getHelp(id: string, categoryId: string): Observable<HelpItem> {
        return this.http.get<HelpItem>(`/api/help/getHelp/${categoryId}/${id}`);
    }

    getHelpItem(spec: HelpQuerySpec) : Observable<HelpItem> {
        return this.http.post<HelpItem>("/api/help/getHelpItems", spec);
    }

    getNavigation(includeDeleted: boolean): Observable<HelpNavigationItem[]> {
        return this.http.get<HelpNavigationItem[]>(`/api/help/getNavigation/${includeDeleted}`);
    }

    saveCategory(category: HelpCategory): Observable<HelpCategory> {
        return this.http.post<HelpCategory>("/api/help/saveCategory", category);
    }

    private saveHelpItem(item: HelpItem): Observable<HelpItem> {
        return this.http.post<HelpItem>("/api/help/saveItem", item);
    }
}
